import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  IconButton,
  Avatar,
  Chip,
  Typography,
  CircularProgress,
  Alert,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Search,
  Visibility,
  Block,
  CheckCircle,
  Warning,
} from '@mui/icons-material';
import adminService from '../../../services/admin.service';

interface User {
  userId: string;
  fullname: string;
  email: string;
  profileImage: string;
  schoolName: string;
  city: string;
  isBanned: boolean;
  isActive: boolean;
  createdAt: string;
}

const UserList: React.FC = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalUsers, setTotalUsers] = useState(0);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  useEffect(() => {
    fetchUsers();
  }, [page, rowsPerPage, search, sortBy, sortOrder]);

  const fetchUsers = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await adminService.getAllUsers({
        page: page + 1,
        limit: rowsPerPage,
        search,
        sortBy,
        sortOrder,
      });

      if (response.status) {
        const usersList = response.data?.users || response.data || [];
        // Backend pagination.totalUsers veya pagination.total olarak dönebilir
        const total = response.data?.pagination?.totalUsers || response.data?.pagination?.total || usersList.length || 0;
        
        setUsers(usersList);
        setTotalUsers(total);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Kullanıcılar yüklenirken hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
    setPage(0);
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        Kullanıcı Yönetimi
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Tüm kullanıcıları görüntüleyin ve yönetin.
      </Typography>

      {/* Filters */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 3, border: '1px solid #e0e0e0' }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            placeholder="İsim, email veya userId ile ara..."
            value={search}
            onChange={handleSearchChange}
            sx={{ flex: 1, minWidth: 250 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />

          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Sıralama</InputLabel>
            <Select
              value={sortBy}
              label="Sıralama"
              onChange={(e) => setSortBy(e.target.value)}
            >
              <MenuItem value="createdAt">Kayıt Tarihi</MenuItem>
              <MenuItem value="fullname">İsim</MenuItem>
              <MenuItem value="email">Email</MenuItem>
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 120 }}>
            <InputLabel>Yön</InputLabel>
            <Select
              value={sortOrder}
              label="Yön"
              onChange={(e) => setSortOrder(e.target.value as 'asc' | 'desc')}
            >
              <MenuItem value="desc">Azalan</MenuItem>
              <MenuItem value="asc">Artan</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Table */}
      <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid #e0e0e0' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                <TableCell>Kullanıcı</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Okul</TableCell>
                <TableCell>Şehir</TableCell>
                <TableCell>Durum</TableCell>
                <TableCell>Kayıt Tarihi</TableCell>
                <TableCell align="right">İşlemler</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                    <Typography color="text.secondary">
                      Kullanıcı bulunamadı.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => {
                  if (!user || !user.userId) {
                    console.warn('Invalid user object:', user);
                    return null;
                  }
                  
                  return (
                  <TableRow
                    key={user.userId}
                    hover
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar src={user.profileImage || ''} alt={user.fullname || 'User'}>
                          {user.fullname?.[0]?.toUpperCase() || 'U'}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight="600">
                            {user.fullname || 'İsimsiz'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {user.userId}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>{user.email || '-'}</TableCell>
                    <TableCell>{user.schoolName || '-'}</TableCell>
                    <TableCell>{user.city || '-'}</TableCell>
                    <TableCell>
                      {user.isBanned ? (
                        <Chip
                          icon={<Block />}
                          label="Banlı"
                          color="error"
                          size="small"
                        />
                      ) : user.isActive ? (
                        <Chip
                          icon={<CheckCircle />}
                          label="Aktif"
                          color="success"
                          size="small"
                        />
                      ) : (
                        <Chip
                          icon={<Warning />}
                          label="Pasif"
                          color="warning"
                          size="small"
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      {user.createdAt ? new Date(user.createdAt).toLocaleDateString('tr-TR') : '-'}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        color="primary"
                        onClick={() => {
                          if (user.userId) {
                            navigate(`/admin/users/${user.userId}`);
                          }
                        }}
                      >
                        <Visibility />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={totalUsers}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Sayfa başına satır:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
        />
      </Paper>
    </Container>
  );
};

export default UserList;

